import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { captureRefFromUrl } from "@/lib/referrals";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import AuthGuard from "@/components/AuthGuard";
import AfriToPlyxRibbon from "@/components/AfriToPlyxRibbon";

import Landing from "@/pages/Landing";
import PlyxLanding from "@/pages/PlyxLanding";
import PlyxFeed from "@/pages/plyx/PlyxFeed";
import PlyxRoot from "@/pages/plyx/PlyxRoot";
import PlyxLogin from "@/pages/plyx/PlyxLogin";
import PlyxRegister from "@/pages/plyx/PlyxRegister";
import PlyxAdminLogin from "@/pages/plyx/PlyxAdminLogin";
import PlyxForgot from "@/pages/plyx/PlyxForgot";
import PlyxReset from "@/pages/plyx/PlyxReset";
import PlyxUpload from "@/pages/plyx/PlyxUpload";
import PlyxProfile from "@/pages/plyx/PlyxProfile";
import PlyxSettings from "@/pages/plyx/PlyxSettings";
import PlyxSellerOnboarding from "@/pages/plyx/PlyxSellerOnboarding";
import PlyxOrderSuccess from "@/pages/plyx/PlyxOrderSuccess";
import PlyxWebhookSettings from "@/pages/plyx/PlyxWebhookSettings";
import PlyxExplore from "@/pages/plyx/PlyxExplore";
import PlyxWallet from "@/pages/plyx/PlyxWallet";
import PlyxPro from "@/pages/plyx/PlyxPro";
import PlyxCreatorInsights from "@/pages/plyx/PlyxCreatorInsights";
import PlyxJoin from "@/pages/plyx/PlyxJoin";
import PlyxVideoView from "@/pages/plyx/PlyxVideoView";
import PlyxPress from "@/pages/plyx/PlyxPress";
import PlyxEmbed from "@/pages/plyx/PlyxEmbed";
import CyberGuardBanner from "@/components/CyberGuardBanner";
import PlyxPromo from "@/pages/plyx/PlyxPromo";
import PlyxErrorBoundary from "@/components/plyx/PlyxErrorBoundary";
import PlyxRipplesListener from "@/components/plyx/PlyxRipplesListener";
import PlyxInbox from "@/pages/plyx/PlyxInbox";
import PlyxDiscover from "@/pages/plyx/PlyxDiscover";
import PlyxMillions from "@/pages/plyx/PlyxMillions";
import PlyxMillionsWatch from "@/pages/plyx/PlyxMillionsWatch";
import PlyxMillionsBattle from "@/pages/plyx/PlyxMillionsBattle";
import PlyxGamesHub from "@/pages/plyx/PlyxGamesHub";
import PlyxBrainClash from "@/pages/plyx/PlyxBrainClash";
import PlyxClashRoom from "@/pages/plyx/PlyxClashRoom";
import PlyxSurvivor from "@/pages/plyx/PlyxSurvivor";
import PlyxKingdoms from "@/pages/plyx/PlyxKingdoms";
import PlyxNetwork from "@/pages/plyx/PlyxNetwork";
import PlyxBottomNav from "@/components/plyx/PlyxBottomNav";
import PlyxShop from "@/pages/plyx/PlyxShop";
import PlyxShopManage from "@/pages/plyx/PlyxShopManage";
import PlyxChallenges from "@/pages/plyx/PlyxChallenges";
import PlyxChallengeDetail from "@/pages/plyx/PlyxChallengeDetail";
import PlyxChallengeNew from "@/pages/plyx/PlyxChallengeNew";
import PlyxLeaderboard from "@/pages/plyx/PlyxLeaderboard";
import PlyxTalent from "@/pages/plyx/PlyxTalent";
import PlyxDuet from "@/pages/plyx/PlyxDuet";
import PlyxCam from "@/pages/plyx/PlyxCam";
import PlyxStudio from "@/pages/plyx/PlyxStudio";
import PlyxAILab from "@/pages/plyx/PlyxAILab";
import PlyxMakers from "@/pages/plyx/PlyxMakers";
import PlyxMakerDetail from "@/pages/plyx/PlyxMakerDetail";
import PlyxTagPage from "@/pages/plyx/PlyxTagPage";
import PlyxSoundPage from "@/pages/plyx/PlyxSoundPage";
import PlyxDMInbox from "@/pages/plyx/PlyxDMInbox";
import PlyxDMThread from "@/pages/plyx/PlyxDMThread";
import PlyxLive from "@/pages/plyx/PlyxLive";
import PlyxLiveNew from "@/pages/plyx/PlyxLiveNew";
import PlyxLiveViewer from "@/pages/plyx/PlyxLiveViewer";
import ReferralLeaderboard from "@/pages/ReferralLeaderboard";
import PlyxHeader from "@/components/plyx/PlyxHeader";
import { PlyxAuthProvider } from "@/context/PlyxAuthContext";
import Browse from "@/pages/Browse";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Forgot from "@/pages/Forgot";
import Reset from "@/pages/Reset";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Verify from "@/pages/Verify";
import Admin from "@/pages/Admin";
import Pro from "@/pages/Pro";
import MapView from "@/pages/MapView";
import LogoCandidates from "@/pages/LogoCandidates";
import PublicStats from "@/pages/PublicStats";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminPlyxNps from "@/pages/AdminPlyxNps";
import AdminPlyxHealth from "@/pages/AdminPlyxHealth";
import AdminPlyxDashboard from "@/pages/AdminPlyxDashboard";
import AdminPlyxAudit from "@/pages/AdminPlyxAudit";
import AdminPlyxFeatured from "@/pages/AdminPlyxFeatured";
import AdminPlyxTalent from "@/pages/AdminPlyxTalent";
import AdminPlyxSellers from "@/pages/AdminPlyxSellers";
import Complaints from "@/pages/Complaints";
import ComplaintNew from "@/pages/ComplaintNew";
import ComplaintDetail from "@/pages/ComplaintDetail";
import AdminComplaints from "@/pages/AdminComplaints";
import BuyerGuarantee from "@/pages/BuyerGuarantee";
import ShareAd from "@/pages/ShareAd";
import CategoryLanding from "@/pages/CategoryLanding";
import LocationLanding from "@/pages/LocationLanding";
import LocationsIndex from "@/pages/LocationsIndex";
import NearMe from "@/pages/NearMe";
import Payouts from "@/pages/Payouts";
import Purchases from "@/pages/Purchases";
import Sales from "@/pages/Sales";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Contact from "@/pages/Contact";
import OrderTrack from "@/pages/OrderTrack";
import Makers from "@/pages/Makers";
import MakerNew from "@/pages/MakerNew";
import MakerDetail from "@/pages/MakerDetail";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import PlyxInstallPrompt from "@/components/plyx/PlyxInstallPrompt";
import PlyxInstallFAB from "@/components/plyx/PlyxInstallFAB";
import PlyxCelebration from "@/components/plyx/PlyxCelebration";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import NewsletterPopup from "@/components/NewsletterPopup";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import PlyxSEO from "@/components/plyx/PlyxSEO";
import AfriMarketSEO from "@/components/AfriMarketSEO";

// Detect OAuth callback (session_id in URL fragment) before normal routing.
const AppRouter = () => {
  const location = useLocation();
  // Capture ?ref= attribution on every navigation (cheap localStorage write)
  useEffect(() => { captureRefFromUrl(); }, [location.pathname, location.search]);
  // GA4 SPA page-view tracking — fire on every route change.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  // CRITICAL: handle session_id during render to avoid race with ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Plyx standalone preview mode — the app behaves as if Plyx is the entire
  // product (all AfriMarket routes redirect to /plyx, cross-promo hidden).
  // Triggered by visiting any URL with ?standalone=plyx (persists in
  // localStorage). To exit: ?standalone=off
  const standalonePlyx = isPlyxStandaloneMode();

  // Marketplace (Shop) paths stay reachable even in Plyx standalone mode so
  // the bottom-nav "Shop" tab actually opens the marketplace to buy & sell.
  const MARKETPLACE_PREFIXES = [
    "/browse", "/listing", "/create", "/dashboard", "/map", "/payment",
    "/orders", "/verify", "/pro", "/buyer-guarantee", "/african-",
    "/near-me", "/locations", "/complaints", "/press",
  ];
  const isMarketplacePath = MARKETPLACE_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p)
  );

  // Plyx renders as a standalone brand experience — no AfriMarket navbar / footer / popups.
  const isPlyx = location.pathname.startsWith("/plyx") || (standalonePlyx && !isMarketplacePath);
  // Feed is full-screen immersive — even Plyx header is suppressed on /plyx/feed exactly.
  const isPlyxFeed = location.pathname === "/plyx/feed" || (standalonePlyx && location.pathname === "/");
  // Single-video page also takes over the full screen (TikTok-style
  // immersive view), so we hide the global PlyxHeader the same way we
  // do for /plyx/feed. Otherwise the header covers the back button and
  // makes the user feel "the feed is gone" when they're really just
  // viewing one video.
  const isPlyxImmersive = isPlyxFeed || /^\/plyx\/(v|embed)\//.test(location.pathname) || location.pathname === "/plyx/cam";

  // In standalone mode, the app renders ONLY Plyx routes. Anything else
  // bounces to /plyx so visitors can never accidentally land on AfriMarket.
  // Admin paths are exempt — they're internal tools and must stay reachable.
  if (
    standalonePlyx
    && !location.pathname.startsWith("/plyx")
    && !location.pathname.startsWith("/admin")
    && !isMarketplacePath
  ) {
    return <Navigate to="/plyx" replace />;
  }

  return (
    <>
      {isPlyx ? <PlyxSEO /> : <AfriMarketSEO />}
      {/* Cyber Guard launch banner — every non-feed Plyx surface + the
          AfriMarket landing page. The immersive feed stays clean. */}
      {!isPlyxImmersive && (isPlyx || standalonePlyx || location.pathname === "/") && <CyberGuardBanner />}
      {!isPlyx && !standalonePlyx && <AfriToPlyxRibbon />}
      {!isPlyx && <Navbar />}
      {isPlyx && !isPlyxImmersive && <PlyxHeader />}
      {(isPlyx || standalonePlyx) && <PlyxBottomNav />}
      <main className={isPlyx ? "" : "min-h-[60vh]"}>
        <PlyxErrorBoundary scope={isPlyx ? "plyx" : "afri"}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/plyx" element={<PlyxRoot />} />
          <Route path="/plyx/feed" element={<PlyxFeed />} />
          <Route path="/plyx/explore" element={<PlyxExplore />} />
          <Route path="/plyx/about" element={<PlyxLanding />} />
          <Route path="/plyx/login" element={<PlyxLogin />} />
          <Route path="/plyx/register" element={<PlyxRegister />} />
          <Route path="/plyx/admin/login" element={<PlyxAdminLogin />} />
          <Route path="/plyx/forgot" element={<PlyxForgot />} />
          <Route path="/plyx/reset" element={<PlyxReset />} />
          <Route path="/plyx/upload" element={<PlyxUpload />} />
          <Route path="/plyx/settings" element={<PlyxSettings />} />
          <Route path="/plyx/seller" element={<PlyxSellerOnboarding />} />
          <Route path="/plyx/order/success" element={<PlyxOrderSuccess />} />
          <Route path="/plyx/settings/webhooks" element={<PlyxWebhookSettings />} />
          <Route path="/plyx/wallet" element={<PlyxWallet />} />
          <Route path="/plyx/pro" element={<PlyxPro />} />
          <Route path="/plyx/insights" element={<PlyxCreatorInsights />} />
          <Route path="/plyx/join" element={<PlyxJoin />} />
          <Route path="/join" element={<PlyxJoin />} />
          <Route path="/plyx/v/:videoId" element={<PlyxVideoView />} />
          <Route path="/plyx/embed/:videoId" element={<PlyxEmbed />} />
          <Route path="/plyx/press" element={<PlyxPress />} />
          <Route path="/press" element={<Navigate to="/plyx/press" replace />} />
          <Route path="/plyx/inbox" element={<PlyxInbox />} />
          <Route path="/plyx/discover" element={<PlyxDiscover />} />
          <Route path="/plyx/millions" element={<PlyxMillions />} />
          <Route path="/plyx/millions/watch/:sessionId" element={<PlyxMillionsWatch />} />
          <Route path="/plyx/millions/battle/:battleId" element={<PlyxMillionsBattle />} />
          <Route path="/plyx/games" element={<PlyxGamesHub />} />
          <Route path="/plyx/clash" element={<PlyxBrainClash />} />
          <Route path="/plyx/clash/room/:code" element={<PlyxClashRoom />} />
          <Route path="/plyx/survivor" element={<PlyxSurvivor />} />
          <Route path="/plyx/kingdoms" element={<PlyxKingdoms />} />
          <Route path="/plyx/u/:handle/network" element={<PlyxNetwork />} />
          <Route path="/plyx/v/:videoId/promo" element={<PlyxPromo />} />
          <Route path="/plyx/promo" element={<PlyxPromo />} />
          <Route path="/plyx/shop/manage" element={<PlyxShopManage />} />
          <Route path="/plyx/u/:handle/shop" element={<PlyxShop />} />
          <Route path="/plyx/challenges" element={<PlyxChallenges />} />
          <Route path="/plyx/challenges/new" element={<PlyxChallengeNew />} />
          <Route path="/plyx/challenges/:id" element={<PlyxChallengeDetail />} />
          <Route path="/plyx/leaderboard" element={<PlyxLeaderboard />} />
          <Route path="/plyx/talent" element={<PlyxTalent />} />
          <Route path="/plyx/duet/:videoId" element={<PlyxDuet />} />
          <Route path="/plyx/cam" element={<PlyxCam />} />
          <Route path="/plyx/studio" element={<PlyxStudio />} />
          <Route path="/plyx/ai-lab" element={<PlyxAILab />} />
          <Route path="/plyx/makers" element={<PlyxMakers />} />
          <Route path="/plyx/makers/:id" element={<PlyxMakerDetail />} />
          <Route path="/plyx/tag/:slug" element={<PlyxTagPage />} />
          <Route path="/plyx/sound/:soundId" element={<PlyxSoundPage />} />
          <Route path="/plyx/dm" element={<PlyxDMInbox />} />
          <Route path="/plyx/dm/:conversationId" element={<PlyxDMThread />} />
          <Route path="/plyx/live" element={<PlyxLive />} />
          <Route path="/plyx/live/new" element={<PlyxLiveNew />} />
          <Route path="/plyx/live/:roomName" element={<PlyxLiveViewer />} />
          <Route path="/plyx/u/:handle" element={<PlyxProfile />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/map" element={<MapView />} />
          <Route path="/logos" element={<LogoCandidates />} />
          <Route path="/stats" element={<PublicStats />} />
          <Route path="/leaderboard" element={<ReferralLeaderboard />} />
          <Route path="/buyer-guarantee" element={<BuyerGuarantee />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/orders/:ref/track" element={<OrderTrack />} />
          <Route path="/makers" element={<Makers />} />
          <Route path="/makers/new" element={<AuthGuard><MakerNew /></AuthGuard>} />
          <Route path="/makers/:id" element={<MakerDetail />} />
          <Route path="/share-ad" element={<ShareAd />} />
          <Route path="/african-fashion" element={<CategoryLanding />} />
          <Route path="/african-beauty" element={<CategoryLanding />} />
          <Route path="/african-food" element={<CategoryLanding />} />
          <Route path="/african-services" element={<CategoryLanding />} />
          <Route path="/african-home" element={<CategoryLanding />} />
          <Route path="/african-gifts" element={<CategoryLanding />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/plyx" element={<AdminPlyxDashboard />} />
          <Route path="/admin/plyx/nps" element={<AuthGuard><AdminPlyxNps /></AuthGuard>} />
          <Route path="/admin/plyx/health" element={<AuthGuard><AdminPlyxHealth /></AuthGuard>} />
          <Route path="/admin/plyx/audit" element={<AdminPlyxAudit />} />
          <Route path="/admin/plyx/featured" element={<AdminPlyxFeatured />} />
          <Route path="/admin/plyx/talent" element={<AdminPlyxTalent />} />
          <Route path="/admin/plyx/sellers" element={<AuthGuard><AdminPlyxSellers /></AuthGuard>} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<Forgot />} />
          <Route path="/reset-password" element={<Reset />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/pro" element={<Pro />} />
          <Route path="/verify" element={<AuthGuard><Verify /></AuthGuard>} />
          <Route path="/admin" element={<AuthGuard><Admin /></AuthGuard>} />
          <Route path="/admin/complaints" element={<AuthGuard><AdminComplaints /></AuthGuard>} />
          <Route path="/complaints" element={<AuthGuard><Complaints /></AuthGuard>} />
          <Route path="/complaints/new" element={<AuthGuard><ComplaintNew /></AuthGuard>} />
          <Route path="/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
          <Route path="/create" element={<AuthGuard><CreateListing /></AuthGuard>} />
          <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
          <Route path="/dashboard/messages" element={<AuthGuard><Messages /></AuthGuard>} />
          <Route path="/dashboard/payouts" element={<AuthGuard><Payouts /></AuthGuard>} />
          <Route path="/dashboard/purchases" element={<AuthGuard><Purchases /></AuthGuard>} />
          <Route path="/dashboard/sales" element={<AuthGuard><Sales /></AuthGuard>} />
          <Route path="/dashboard/my-listings" element={<Navigate to="/dashboard" replace />} />
          {/* ── Location-first navigation (must come AFTER all static routes) ── */}
          <Route path="/locations" element={<LocationsIndex />} />
          <Route path="/near-me" element={<NearMe />} />
          <Route path="/:country" element={<LocationLanding />} />
          <Route path="/:country/:city" element={<LocationLanding />} />
          <Route path="/:country/:city/:area" element={<LocationLanding />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PlyxErrorBoundary>
      </main>
      {!isPlyx && <Footer />}
      {!isPlyx && <InstallAppPrompt />}
      {isPlyx && <PlyxInstallPrompt />}
      {isPlyx && <PlyxInstallFAB />}
      {isPlyx && <PlyxCelebration />}
      {isPlyx && <PlyxRipplesListener />}
      {!isPlyx && <CookieConsentBanner />}
      {!isPlyx && !standalonePlyx && <NewsletterPopup />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <PlyxAuthProvider>
              <CurrencyProvider>
                <AppRouter />
                <Toaster position="top-right" richColors />
              </CurrencyProvider>
            </PlyxAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
